import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Input } from '@flintmint/web-shared/components/input';

import { constraintValue } from '@/utils/helper-utils';

import styles from './numeric-stepper.module.scss';

export const NumericStepper = (props: NumericStepperProps) => {
  const { value, min = -Infinity, max = Infinity, onChange, ...rest } = props;

  const [localValue, setLocalValue] = useState<number | string>(value);

  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);

  const onBlur = () => {
    const validNumber = localValue && /^[\d.]*$/.test(`${localValue}`) && !isNaN(Number(localValue));
    if (validNumber) {
      const val = constraintValue(localValue, min, max);
      onChange(val);
      setLocalValue(val);
    } else {
      setLocalValue(constraintValue(value, min, max));
    }
  };

  return (
    <Input
      gap="medium"
      inputRef={ref}
      wrapperAs="div"
      value={localValue}
      appearance="minimal"
      className={clsx(styles.numericStepper, 'editor-numeric-stepper')}
      left={
        <button
          type="button"
          className={styles.icon}
          aria-label="Decrease font size"
          onClick={() => {
            onChange(constraintValue(value - 1, min, max));
          }}
        >
          <Icon type="minus-sign" size={16} />
        </button>
      }
      right={
        <button
          type="button"
          className={styles.icon}
          aria-label="Increase font size"
          onClick={() => {
            onChange(constraintValue(value + 1, min, max));
          }}
        >
          <Icon type="plus" size={16} />
        </button>
      }
      onBlur={onBlur}
      aria-label="Font size"
      onKeyDown={(e) => e.key === 'Enter' && ref.current?.blur()}
      onChange={(e) => {
        if (/^\d*[.,]?\d*$/.test(e.target.value)) {
          setLocalValue(e.target.value.replace(',', '.'));
        }
      }}
      {...rest}
      inputProps={{ size: 2, className: styles.value }}
    />
  );
};

interface NumericStepperProps {
  min?: number;
  max?: number;
  value: number;
  onChange: (value: number) => void;
}
